import type { SpacingToken, NegativeSpacingToken, RadiusToken } from '../../../themes/types';
import { spacing } from '../../../sizes/spacing';
import type { StyleResolverContext, CssFragment } from './index';

// ============================================================================
// Types
// ============================================================================

/** Spacing value: token, pixel number, or 'full' for 100%. */
export type SpacingValue = SpacingToken | number | 'full';

/** Margin value: same as spacing, plus 'auto' and negative tokens. */
export type MarginValue = SpacingToken | NegativeSpacingToken | number | 'auto' | 'full';

export interface PaddingProps {
  p?: SpacingValue;
  px?: SpacingValue;
  py?: SpacingValue;
  pl?: SpacingValue;
  pr?: SpacingValue;
  pt?: SpacingValue;
  pb?: SpacingValue;
}

export interface MarginProps {
  m?: MarginValue;
  mx?: MarginValue;
  my?: MarginValue;
  ml?: MarginValue;
  mr?: MarginValue;
  mt?: MarginValue;
  mb?: MarginValue;
}

// ============================================================================
// Spacing value resolution
// ============================================================================

const spacingTokens = new Set(Object.keys(spacing));

const isSpacingToken = (value: string): value is SpacingToken => spacingTokens.has(value);

export const resolveSpacingValue = (
  value: SpacingValue | undefined,
  getSpacing: (token: SpacingToken) => string,
): string | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === 'number') return `${value}px`;
  if (typeof value === 'string') {
    if (value === 'full') return '100%';
    if (isSpacingToken(value)) return getSpacing(value);
    return value;
  }
  return undefined;
};

const resolveMarginValue = (
  value: MarginValue | undefined,
  getSpacing: (token: SpacingToken) => string,
): string | undefined => {
  if (value === undefined) return undefined;
  if (value === 'auto') return 'auto';
  if (typeof value === 'string' && value.startsWith('-')) {
    const positiveToken = value.slice(1);
    if (isSpacingToken(positiveToken)) return `-${getSpacing(positiveToken)}`;
  }
  return resolveSpacingValue(value as SpacingValue, getSpacing);
};

// ============================================================================
// Padding
// ============================================================================

const resolvePaddingCss = (props: PaddingProps, getSpacing: (token: SpacingToken) => string): CssFragment[] => {
  const { p, px, py, pl, pr, pt, pb } = props;
  const pAll = resolveSpacingValue(p, getSpacing);
  const pX = resolveSpacingValue(px, getSpacing);
  const pY = resolveSpacingValue(py, getSpacing);
  const paddingTop = resolveSpacingValue(pt, getSpacing) ?? pY ?? pAll;
  const paddingBottom = resolveSpacingValue(pb, getSpacing) ?? pY ?? pAll;
  const paddingLeft = resolveSpacingValue(pl, getSpacing) ?? pX ?? pAll;
  const paddingRight = resolveSpacingValue(pr, getSpacing) ?? pX ?? pAll;
  return [
    paddingTop ? `padding-top: ${paddingTop};` : null,
    paddingBottom ? `padding-bottom: ${paddingBottom};` : null,
    paddingLeft ? `padding-left: ${paddingLeft};` : null,
    paddingRight ? `padding-right: ${paddingRight};` : null,
  ];
};

export const resolvePaddingStyleCss = (props: Record<string, unknown>, theme: StyleResolverContext): CssFragment[] => {
  const { p, px, py, pl, pr, pt, pb } = props as Partial<PaddingProps>;
  return resolvePaddingCss({ p, px, py, pl, pr, pt, pb }, theme.getSpacing);
};

// ============================================================================
// Margin
// ============================================================================

const resolveMarginCss = (props: MarginProps, getSpacing: (token: SpacingToken) => string): CssFragment[] => {
  const { m, mx, my, ml, mr, mt, mb } = props;
  const mAll = resolveMarginValue(m, getSpacing);
  const mX = resolveMarginValue(mx, getSpacing);
  const mY = resolveMarginValue(my, getSpacing);
  const marginTop = resolveMarginValue(mt, getSpacing) ?? mY ?? mAll;
  const marginBottom = resolveMarginValue(mb, getSpacing) ?? mY ?? mAll;
  const marginLeft = resolveMarginValue(ml, getSpacing) ?? mX ?? mAll;
  const marginRight = resolveMarginValue(mr, getSpacing) ?? mX ?? mAll;
  return [
    marginTop ? `margin-top: ${marginTop};` : null,
    marginBottom ? `margin-bottom: ${marginBottom};` : null,
    marginLeft ? `margin-left: ${marginLeft};` : null,
    marginRight ? `margin-right: ${marginRight};` : null,
  ];
};

export const resolveMarginStyleCss = (props: Record<string, unknown>, theme: StyleResolverContext): CssFragment[] => {
  const { m, mx, my, ml, mr, mt, mb } = props as Partial<MarginProps>;
  return resolveMarginCss({ m, mx, my, ml, mr, mt, mb }, theme.getSpacing);
};

// ============================================================================
// Radius
// ============================================================================

export interface RadiusProps {
  borderRadius?: RadiusToken;
  borderRadiusT?: RadiusToken;
  borderRadiusB?: RadiusToken;
  borderRadiusTL?: RadiusToken;
  borderRadiusTR?: RadiusToken;
  borderRadiusBL?: RadiusToken;
  borderRadiusBR?: RadiusToken;
}

export interface ResolvedRadius {
  borderRadius: string | undefined;
  borderRadiusTL: string | undefined;
  borderRadiusTR: string | undefined;
  borderRadiusBL: string | undefined;
  borderRadiusBR: string | undefined;
}

const resolveRadius = (props: RadiusProps, getRadius: (token: RadiusToken) => string): ResolvedRadius => {
  const { borderRadius, borderRadiusT, borderRadiusB, borderRadiusTL, borderRadiusTR, borderRadiusBL, borderRadiusBR } =
    props;
  const radiusT = borderRadiusT ? getRadius(borderRadiusT) : undefined;
  const radiusB = borderRadiusB ? getRadius(borderRadiusB) : undefined;

  return {
    borderRadius: borderRadius ? getRadius(borderRadius) : undefined,
    borderRadiusTL: borderRadiusTL ? getRadius(borderRadiusTL) : radiusT,
    borderRadiusTR: borderRadiusTR ? getRadius(borderRadiusTR) : radiusT,
    borderRadiusBL: borderRadiusBL ? getRadius(borderRadiusBL) : radiusB,
    borderRadiusBR: borderRadiusBR ? getRadius(borderRadiusBR) : radiusB,
  };
};

// ============================================================================
// Layout (gap + radius) — style resolver
// ============================================================================

export interface LayoutProps extends RadiusProps {
  gap?: SpacingValue;
}

export const resolveLayoutStyleCss = (props: Record<string, unknown>, theme: StyleResolverContext): CssFragment[] => {
  const {
    gap: gapProp,
    borderRadius,
    borderRadiusT,
    borderRadiusB,
    borderRadiusTL,
    borderRadiusTR,
    borderRadiusBL,
    borderRadiusBR,
  } = props as Partial<LayoutProps>;

  const gap = resolveSpacingValue(gapProp, theme.getSpacing);
  const r = resolveRadius(
    { borderRadius, borderRadiusT, borderRadiusB, borderRadiusTL, borderRadiusTR, borderRadiusBL, borderRadiusBR },
    theme.getRadius,
  );

  return [
    gap ? `gap: ${gap};` : null,
    r.borderRadius ? `border-radius: ${r.borderRadius};` : null,
    r.borderRadiusTL ? `border-top-left-radius: ${r.borderRadiusTL};` : null,
    r.borderRadiusTR ? `border-top-right-radius: ${r.borderRadiusTR};` : null,
    r.borderRadiusBL ? `border-bottom-left-radius: ${r.borderRadiusBL};` : null,
    r.borderRadiusBR ? `border-bottom-right-radius: ${r.borderRadiusBR};` : null,
  ];
};
